/**
 * useOriginsPage - Hook para orquestrar a página de Origens
 * Separa lógica de estado e handlers do componente de apresentação.
 */
import { useState, useCallback, useMemo } from 'react';
import { useOrigins } from './useOrigins';
import { Origin } from '../../../../types';
import { notify } from '../../../../utils/toast';
import { TrashIcon } from '../../../../components/icons';
import { createImportAction, createExportAction } from '../../../../components/shared/ActionMenu';
import { BulkAction } from '../../../../components/shared/BulkActionsBar';

export const useOriginsPage = () => {
    const {
        origins,
        loading,
        createOrigin,
        updateOrigin,
        deleteOrigin,
        deleteBulk,
        selectedIds,
        toggleSelection,
        toggleAllSelection,
        clearSelection,
        refreshOrigins
    } = useOrigins();

    const [searchTerm, setSearchTerm] = useState('');
    const [isFormModalOpen, setIsFormModalOpen] = useState(false);
    const [originToEdit, setOriginToEdit] = useState<Origin | undefined>(undefined);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [originToDelete, setOriginToDelete] = useState<Origin | undefined>(undefined);
    const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);

    const filteredOrigins = useMemo(() =>
        origins.filter(origin =>
            origin.name.toLowerCase().includes(searchTerm.toLowerCase())
        ), [origins, searchTerm]);

    const handleOpenCreateModal = useCallback(() => {
        setOriginToEdit(undefined);
        setIsFormModalOpen(true);
    }, []);

    const handleOpenEditModal = useCallback((origin: Origin) => {
        setOriginToEdit(origin);
        setIsFormModalOpen(true);
    }, []);

    const handleOpenDeleteModal = useCallback((origin: Origin) => {
        setOriginToDelete(origin);
        setIsDeleteModalOpen(true);
    }, []);

    const handleSaveOrigin = useCallback(async (data: Omit<Origin, 'id' | 'createdAt'>) => {
        try {
            if (originToEdit) {
                await updateOrigin(originToEdit.id, data);
                notify.success('Origem atualizada com sucesso!');
            } else {
                await createOrigin(data);
                notify.success('Origem criada com sucesso!');
            }
            setIsFormModalOpen(false);
        } catch (error) {
            notify.error('Erro ao salvar origem');
        }
    }, [originToEdit, updateOrigin, createOrigin]);

    const handleConfirmDelete = useCallback(async () => {
        if (originToDelete) {
            try {
                await deleteOrigin(originToDelete.id);
                notify.success('Origem excluída com sucesso!');
                setIsDeleteModalOpen(false);
                setOriginToDelete(undefined);
            } catch (error) {
                notify.error('Erro ao excluir origem');
            }
        }
    }, [originToDelete, deleteOrigin]);

    const handleBulkDelete = useCallback(async () => {
        const ids = Array.from(selectedIds);
        try {
            await deleteBulk(ids);
            notify.success(`${ids.length} origem(ns) excluída(s) com sucesso!`);
            setIsBulkDeleteModalOpen(false);
        } catch {
            notify.error('Erro ao excluir origens');
        }
    }, [selectedIds, deleteBulk]);

    const handleDuplicate = useCallback(async (origin: Origin) => {
        try {
            const { id, createdAt, ...data } = origin;
            await createOrigin({ ...data, name: `${data.name} (Cópia)` });
            notify.success('Origem duplicada com sucesso!');
        } catch (error) {
            notify.error('Erro ao duplicar origem');
        }
    }, [createOrigin]);

    const handleRefresh = useCallback(async () => {
        await refreshOrigins();
        notify.success('Dados atualizados!');
    }, [refreshOrigins]);

    const headerActions = useMemo(() => [
        createImportAction(() => notify.info('Funcionalidade de importação em desenvolvimento')),
        createExportAction(() => notify.info('Funcionalidade de exportação em desenvolvimento')),
    ], []);

    const bulkActions: BulkAction[] = useMemo(() => [
        {
            label: 'Excluir',
            icon: TrashIcon,
            onClick: () => setIsBulkDeleteModalOpen(true),
            variant: 'danger' as const
        }
    ], []);

    return {
        filteredOrigins,
        loading,
        searchTerm,
        setSearchTerm,
        selectedIds,
        originToEdit,
        originToDelete,
        isFormModalOpen,
        setIsFormModalOpen,
        isDeleteModalOpen,
        setIsDeleteModalOpen,
        isBulkDeleteModalOpen,
        setIsBulkDeleteModalOpen,
        handleOpenCreateModal,
        handleOpenEditModal,
        handleOpenDeleteModal,
        handleSaveOrigin,
        handleConfirmDelete,
        handleBulkDelete,
        handleDuplicate,
        handleRefresh,
        toggleSelection,
        toggleAllSelection,
        clearSelection,
        headerActions,
        bulkActions
    };
};
